/**
 * Plain-language explanations for policy and run reasons (ADR-0054).
 *
 * OPA returns technical deny reasons (`deny contains "…"` in platform/policies/agentplatform/
 * authz/*.rego), the orchestrator adds approval reasons and run failures. Each one maps to an
 * i18n key in the `clarity` namespace with a `why` sentence and a `next` sentence (what to do
 * instead). A unit test parses the Rego files and fails when a reason has no entry here or no
 * Arabic and English text. Unknown reasons fall back to the raw text.
 */
import { ApiError } from "./http";

/** The user's sign-in predates an agent's release; a fresh token fixes it (orchestrator 409). */
export const TOKEN_REFRESH_NEEDED = "token_refresh_needed";
/** A bare server error (5xx without reasons): explained in plain words, raw text kept aside. */
export const PLATFORM_ERROR = "platform_error";

/** `detail.code` of an API error, if any. */
export function apiErrorCode(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null;
  const d = (err.body as { detail?: unknown } | undefined)?.detail;
  return d && typeof d === "object" && typeof (d as { code?: unknown }).code === "string" ? (d as { code: string }).code : null;
}

/**
 * Runs `call`; when it fails with `token_refresh_needed` and a refresh is available, renews the
 * sign-in silently and retries once (a token issued before an agent's release lacks its audience).
 */
export async function retryAfterTokenRefresh<T>(call: () => Promise<T>, refresh: (() => Promise<boolean>) | undefined): Promise<T> {
  try {
    return await call();
  } catch (err) {
    if (apiErrorCode(err) !== TOKEN_REFRESH_NEEDED || !refresh || !(await refresh())) throw err;
    return call();
  }
}

export interface ReasonRule {
  /** The exact reason string, or a pattern for `sprintf` reasons (groups fill `params`). */
  match: string | RegExp;
  /** Key under `clarity:reason.<key>.why` / `.next`. */
  key: string;
  params?: string[];
}

export interface ExplainedReason {
  raw: string;
  key: string;
  values: Record<string, string>;
}

const r = (match: string | RegExp, key: string, params?: string[]): ReasonRule => ({ match, key, ...(params ? { params } : {}) });

export const REASON_RULES: ReasonRule[] = [
  // ---- who may act ------------------------------------------------------------------------
  r("case tasks must be handled by a human user", "human_only"),
  r("case tasks cannot be handled through an agent", "no_agent_on_behalf"),
  r("feedback export requires a user principal", "human_only"),
  r("retrieval requires a user principal", "human_only"),
  // G-camp-cs-01: runs without a person (website visitors) search knowledge shared with everyone.
  r("without a person, knowledge searches may only read documents shared with everyone", "visitor_knowledge_shared"),
  r("without a person, knowledge searches may read Public or Internal documents only", "visitor_knowledge_level"),
  // G-camp-cs-16: personal data the person gave, put back into a tool call.
  r(/^personal data the person gave may not be sent with (.+)$/, "pii_restore_tool", ["tool"]),
  r("this action does not take personal data", "pii_restore_action"),
  r("document reading requires a user principal", "human_only"),
  r("user memory requires a user principal", "human_only"),
  r("consent changes require a user", "human_only"),
  r("human agent replies must come from a user", "human_only"),
  r("kill switch requires a human user", "human_only"),
  r("marketplace actions require a human user", "human_only"),
  r("promotion requires a human user", "human_only"),
  r("user administration requires a human user", "human_only"),
  r("directory lookup requires a human user", "human_only"),
  r("connecting systems requires a human user", "human_only"),
  r("case settings require a human user", "human_only"),
  r("case settings cannot be changed through an agent", "no_agent_on_behalf"),
  r("consents cannot be recorded through an agent", "no_agent_on_behalf"),
  r("human agent replies cannot be sent through an agent", "no_agent_on_behalf"),
  r("outbound requires a user or an agent", "human_only"),
  r("agent acting without a user requires autonomous mode", "agent_needs_user"),
  // ---- four eyes ----------------------------------------------------------------------------
  r("requester may not decide their own request", "four_eyes"),
  r("four-eyes: requester cannot approve own promotion", "four_eyes"),
  r("four-eyes: publisher cannot certify own listing", "four_eyes"),
  // ---- approval chains (ADR-0102) -----------------------------------------------------------
  r("the same person may not approve two levels of one request", "four_eyes_levels"),
  r("you already approved this request", "already_approved"),
  r("this approval is for named people only", "named_approvers"),
  r("a person covering for someone may not approve their own request", "four_eyes"),
  r("cover during leave must be set by the person themselves", "cover_self"),
  // ---- organisation rules (ADR-0077): stricter only, named by the organisation ---------------
  r(/^organisation rule '(.+)': only the people, roles or agents it names may use this$/, "org_rule_who", ["rule"]),
  r(/^organisation rule '(.+)': not allowed at this time$/, "org_rule_time", ["rule"]),
  r(/^organisation rule '(.+)' requires approval$/, "org_rule_approval", ["rule"]),
  r(/^organisation rule '(.+)': data above (.+) is not allowed$/, "org_rule_data", ["rule", "level"]),
  r(/^organisation rule '(.+)': online AI models are not allowed$/, "org_rule_online", ["rule"]),
  r(/^organisation rule '(.+)': the daily limit is reached$/, "org_rule_daily", ["rule"]),
  // ---- missing roles -------------------------------------------------------------------------
  r("user lacks a role allowed to decide this task kind", "role_decide_task"),
  r("user lacks a role required by the tool action", "role_tool_action"),
  r("user lacks the channel-agent role", "role_missing", ["role"]),
  r("outbound by a user requires the channel-campaigner role", "role_missing", ["role"]),
  r("recording consent on behalf of a recipient requires channel-consent-admin", "role_missing", ["role"]),
  r("tenant memory writes require memory-curator role", "role_missing", ["role"]),
  r("kill switch requires kill-switch-operator or platform-admin role", "role_missing", ["role"]),
  r("certification requires marketplace-curator role", "role_missing", ["role"]),
  r("approver lacks release-approver role", "role_missing", ["role"]),
  r("requester lacks builder role", "role_missing", ["role"]),
  r("installer lacks builder or tool-admin role", "role_missing", ["role"]),
  r("publisher lacks builder or tool-admin role", "role_missing", ["role"]),
  r("connecting systems requires tool-admin or platform-admin in the platform tenant", "role_missing", ["role"]),
  r("user administration requires tenant-admin or platform-admin", "role_missing", ["role"]),
  r("changing case settings requires case-admin or tenant-admin", "role_missing", ["role"]),
  r("user may not run this agent", "agent_not_for_user"),
  // ---- platform-admin only ------------------------------------------------------------------
  r("global kill switch requires platform-admin", "platform_admin_only"),
  r("cross-tenant kill switch requires platform-admin", "platform_admin_only"),
  r("cross-tenant user administration requires platform-admin", "platform_admin_only"),
  r("granting a clearance above your own requires platform-admin", "clearance_above_own"),
  r("platform tenant users are managed by platform-admin only", "platform_admin_only"),
  r("users holding platform roles are managed by platform-admin only", "platform_admin_only"),
  r(/^granting or revoking (.+) requires platform-admin$/, "platform_role_grant", ["role"]),
  r(/^(.+) can only be granted to users of the platform tenant$/, "platform_role_tenant", ["role"]),
  r(/^role (.+) is not assignable$/, "role_not_assignable", ["role"]),
  // ---- self-protection ----------------------------------------------------------------------
  r("cannot change own roles", "self_roles"),
  r("cannot disable own account", "self_disable"),
  r("cannot reset own password here; use the account console", "self_password"),
  // ---- kill switches -----------------------------------------------------------------------
  r("global kill switch engaged", "kill_global"),
  r("tenant kill switch engaged", "kill_tenant"),
  r("agent kill switch engaged", "kill_agent"),
  r("kill switch reason required", "reason_required"),
  r("unknown kill switch scope", "invalid_request"),
  // ---- agent state ---------------------------------------------------------------------------
  r("agent not registered", "agent_not_registered"),
  r("agent expired", "agent_expired"),
  r("agent belongs to another tenant", "other_tenant"),
  r("agent not approved for this environment", "agent_not_promoted"),
  r(/^agent status is (.+)$/, "agent_status", ["status"]),
  r(/^agent registry status is (.+)$/, "agent_status", ["status"]),
  r(/^action risk tier (\d+) exceeds agent tier (\d+)$/, "risk_tier", ["needed", "agent"]),
  // ---- data classification -------------------------------------------------------------------
  r("user clearance below data classification", "clearance_low"),
  r("agent data ceiling below data classification", "agent_ceiling"),
  r("data classification exceeds tenant ceiling", "tenant_ceiling"),
  r("data classification exceeds model ceiling", "model_ceiling"),
  r("data classification not allowed to leave the network", "no_egress_data"),
  r("unknown data classification", "invalid_request"),
  r("unknown clearance", "invalid_request"),
  r("unknown action", "invalid_request"),
  // ---- tools --------------------------------------------------------------------------------
  r("tool not specified", "invalid_request"),
  r("tool not registered", "tool_unknown"),
  r("tool action not registered", "tool_unknown"),
  r("tool not granted to agent", "tool_not_granted"),
  r("tool action not granted to agent", "tool_not_granted"),
  r(/^tool is (.+)$/, "tool_status", ["status"]),
  r("side effect not permitted in dry-run", "dry_run"),
  r("connection tests never change data; use a dry run", "dry_run"),
  // ---- approval reasons (why a person must approve) ------------------------------------------
  r("risk tier requires approval", "needs_approval_risk"),
  r("amount above threshold", "needs_approval_amount"),
  r("model confidence below threshold", "needs_approval_confidence"),
  r("influenced by untrusted content", "needs_approval_untrusted"),
  r("tool action always requires approval", "needs_approval_always"),
  // Work tools (ADR-0091).
  r(/^a file made by this agent must be at least (\S+) \(the agent's data level\)$/, "file_level", ["level"]),
  r("a message leaves the platform from an agent that sees data above Internal", "needs_approval_message_out"),
  r("closing a case needs a person's approval", "needs_approval_case_close"),
  r("approval node", "needs_approval_step"),
  r("amount_above_threshold", "needs_approval_amount"),
  r("risk_tier_2_side_effect", "needs_approval_risk"),
  r("untrusted_influence", "needs_approval_untrusted"),
  r("privileged_access", "needs_approval_privileged"),
  // ---- memory, knowledge ---------------------------------------------------------------------
  r("user memory belongs to another user", "not_yours"),
  r("agent memory belongs to another agent", "not_yours"),
  r("memory scope missing", "invalid_request"),
  r("memory write without consent", "memory_consent"),
  r("erasure not permitted", "erasure"),
  r("semantic query requires a model", "invalid_request"),
  // ---- models -------------------------------------------------------------------------------
  r("model not specified", "invalid_request"),
  r("model not registered", "model_unknown"),
  r(/^model not approved for (.+)$/, "model_not_approved", ["env"]),
  r("model provider not allowed for tenant", "model_provider"),
  r("model region outside tenant data residency", "model_residency"),
  r("online models disabled for tenant", "model_online_off"),
  r("egress host not on allow-list", "egress_host"),
  r(/^(.+) is not on this tool's internet allow-list$/, "tool_internet", ["host"]),
  r("outbound request without a host", "tool_internet_no_host"),
  // ADR-0096: an organisation's own websites, and what agents may do on each.
  r(/^(.+) is not on your organisation's allowed websites$/, "org_website", ["host"]),
  r(/^your organisation does not allow (.+) on (.+)$/, "org_website_action", ["what", "host"]),
  // ---- promotion, marketplace ----------------------------------------------------------------
  r("artefact not signed", "not_signed"),
  r("eval gate not passed", "eval_gate"),
  r(/^prod promotion needs (\d+) distinct approvals$/, "prod_approvals", ["n"]),
  r("listing is not shared with this tenant", "listing_not_shared"),
  r("third-party component is not certified for use", "not_certified"),
  r("third-party listing licence is not OSI-approved", "licence"),
  // ---- channels -----------------------------------------------------------------------------
  r("channel disabled for tenant", "channel_off"),
  r("handover to humans disabled for tenant", "handover_off"),
  r("voice is not turned on for this agent", "voice_off"),
  r("voice sessions cannot be opened through an agent", "no_agent_on_behalf"),
  r("the Assistant's voice needs a signed-in person", "human_only"),
  r("outbound purpose not allowed for tenant", "outbound_purpose"),
  r("agent not granted this outbound purpose", "outbound_purpose"),
  r("outbound requires an active consent for the channel and purpose", "outbound_consent"),
  // G-camp-gov-01 (ADR-0109): consent the person gave in the conversation.
  r("an agent may only record consent the person gave in the conversation", "consent_said"),
  r("the person's own words of consent were not found in what they said", "consent_said"),
  r("recipient opted out of this channel", "opted_out"),
  r("outbound blocked by quiet hours", "quiet_hours"),
  r("outbound rate limit exceeded", "rate_limit"),
  // ---- user administration -----------------------------------------------------------------
  r("user administration needs a target tenant", "invalid_request"),
  // ---- directory ----------------------------------------------------------------------------
  r("directory lookup is limited to your own tenant", "other_tenant"),
  // ---- agent work roles (ADR-0101) -------------------------------------------------------------
  r("agent roles are granted by a human user", "human_only"),
  r("agent roles require tenant-admin or agent-admin", "agent_roles_admin"),
  r("another organisation's agents are managed by platform-admin only", "other_tenant"),
  r(/^role (.+) cannot be granted to an agent$/, "agent_role_not_allowed", ["role"]),
  // ---- process map (ADR-0056) ----------------------------------------------------------
  r("process maps are read by people", "human_only"),
  r("reading process maps needs builder, agent-admin, operator, tenant-admin, executive, finance or an auditor role", "process_map_role"),
  r("process map needs a target tenant and agent", "invalid_request"),
  // ---- request story and ask why (ADR-0057) -------------------------------------------------
  r("explaining a request requires a human user", "human_only"),
  r("explaining a request cannot be done through an agent", "no_agent_on_behalf"),
  r("cross-tenant request stories require platform-auditor", "other_tenant"),
  r("only people involved in the request, builders, agent admins, reviewers, operators, auditors and tenant admins may see it", "request_story_access"),
  r("the auditor view requires auditor, platform-auditor or tenant-admin", "auditor_view"),
  r("what a request asked and answered is shown to its requester, builders, agent admins and auditors", "request_content"),
  // ---- builder test runs and triggers (ADR-0062) ----------------------------------------------
  r("only people can try agents or manage their triggers", "human_only"),
  r("agents cannot try agents or manage triggers", "no_agent_on_behalf"),
  r("trying agents and managing triggers needs the builder or agent-admin role", "role_missing", ["role"]),
  r("agents can only be tried in the development environment", "test_dev_only"),
  r("a test run needs the agent under test", "invalid_request"),
  r("process maps of another tenant need platform-auditor", "other_tenant"),
  // ---- activity (ADR-0066) ----------------------------------------------------------------
  r("activity is read by people", "human_only"),
  r("activity needs a target tenant and a scope", "invalid_request"),
  r("everyone's requests are listed for operators, tenant admins and auditors", "activity_list_role"),
  r("activity counts for the whole organisation need an operations, governance or management role", "activity_summary_role"),
  r("activity of another tenant needs platform-auditor", "other_tenant"),
  // ---- run failures (orchestrator) ------------------------------------------------------------
  r(/^cost budget exceeded/, "run_budget"),
  r(/^token budget exceeded/, "run_tokens"),
  r("human wait exceeded maximum duration", "run_wait_timeout"),
  r("runaway", "run_runaway"),
  // RunawayError (orchestrator compiler.py): a step repeated too often, or too many steps.
  r(/^(?:Runaway: )?node (\S+) visited (\d+) times \(limit (\d+)\)$/, "run_loop", ["step", "visits", "limit"]),
  r(/^(?:Runaway: )?max_steps (\d+) exceeded at node \S+$/, "run_runaway", ["limit"]),
  // A step's own time limit (LLM `timeout_s`), and engine time-outs in general.
  r(/^step (\S+) took longer than its time limit \((\d+) ?s\)$/, "run_step_timeout", ["step", "seconds"]),
  r(/TimeoutError|timed out|StartToClose|time limit reached/i, "run_timeout"),
  // Temporal gives up on an activity after its retries; adapters report refused service calls.
  r(/^activity task failed$/i, "run_step_failed"),
  r(/^(?:AdapterError: )?(?:POST|GET|PUT|PATCH|DELETE) \S+ -> HTTP (\d+)$/, "run_service", ["status"]),
  // The agent's permission to act for the person could not be renewed (platform_common identity
  // TokenError at a step, G-life-06): the sign-in system refused it.
  r(/^(?:TokenError: )?(?:refresh failed|delegation exchange failed): HTTP (\d+)/, "run_signin", ["status"]),
  r(/^sub-agent ([^@\s]+)(?:@\S+)? \S+: /, "run_subagent", ["agent"]),
  r(/^decision (\S+): no valid option chosen after (\d+) asks$/, "run_decision_unanswered", ["step", "asks"]),
  r("cancelled by user", "run_cancelled"),
  r("denied_by_policy", "denied_generic"),
  r("denied by policy", "denied_generic"),
  // ---- console-side reasons (no Rego source) ----------------------------------------------
  r(TOKEN_REFRESH_NEEDED, "token_refresh_needed"),
  r(PLATFORM_ERROR, "platform_error"),
];

/** Role names that a `role_missing` reason mentions (first `-`-joined token(s) ending the clause). */
function roleIn(raw: string): string {
  const m = /(?:requires|lacks)(?: the)? ([a-z][a-z0-9-]*(?: or [a-z][a-z0-9-]*)?)(?: role)?$/.exec(raw);
  return m?.[1] ?? "";
}

export function explainReason(raw: string): ExplainedReason | null {
  const text = raw.trim();
  for (const rule of REASON_RULES) {
    if (typeof rule.match === "string") {
      if (rule.match !== text) continue;
      const values: Record<string, string> = {};
      if (rule.params?.includes("role")) values.role = roleIn(text);
      return { raw: text, key: rule.key, values };
    }
    const m = rule.match.exec(text);
    if (!m) continue;
    const values: Record<string, string> = {};
    (rule.params ?? []).forEach((p, i) => {
      values[p] = m[i + 1] ?? "";
    });
    return { raw: text, key: rule.key, values };
  }
  return null;
}

/**
 * Individual reasons inside an error: `{detail: {reasons: [...]}}` (orchestrator),
 * "denied by policy: a; b" (cases, identity) or the message itself.
 */
export function reasonsFrom(err: unknown): string[] {
  if (err instanceof ApiError) {
    const body = err.body as { detail?: { reasons?: unknown } } | undefined;
    const rs = body?.detail?.reasons;
    if (Array.isArray(rs) && rs.every((x) => typeof x === "string")) return rs as string[];
    if (apiErrorCode(err) === TOKEN_REFRESH_NEEDED) return [TOKEN_REFRESH_NEEDED];
    if (err.status >= 500) return [PLATFORM_ERROR];
  }
  const message = err instanceof Error ? err.message : typeof err === "string" ? err : "";
  return splitReasons(message);
}

export function splitReasons(message: string): string[] {
  const m = /^(?:denied by policy|policy denied|forbidden)\s*:\s*(.+)$/i.exec(message.trim());
  const body = m?.[1] ?? message;
  const parts = body
    .split(/;\s+/)
    .map((s) => s.trim())
    .filter(Boolean);
  // Some reasons contain "; " themselves: merge neighbours that only make sense together.
  const out: string[] = [];
  for (let i = 0; i < parts.length; i++) {
    const joined = i + 1 < parts.length ? `${parts[i]}; ${parts[i + 1]}` : null;
    if (joined && explainReason(joined)) {
      out.push(joined);
      i += 1;
    } else out.push(parts[i] as string);
  }
  return out;
}
